import {ChangeEvent, FormEvent, useRef, useState} from "react";
import {Alert, Badge, Button, ButtonGroup, Card, Form, ListGroup, ProgressBar} from "react-bootstrap";
import {ProductsApi} from "../../../api/ProductsApi";
import {ProductDemoViewModel, ProductViewModel} from "../../../api/models";
import {uploadToPresignedUrl} from "../../../api/upload";
import {ErrorAlert} from "../../../components/ErrorAlert";
import {ConfirmModal} from "../../../components/ConfirmModal";
import {SubmitButton} from "../../../components/SubmitButton";
import {formatBytes} from "../../../components/format";
import {analyseFile} from "../../../components/demoAnalysis";

// The API's audio types, by extension: browsers disagree on an m4a's or a FLAC's type (audio/x-m4a, or none at all).
const AUDIO_TYPES: Record<string, string> = {
    mp3: 'audio/mpeg',
    m4a: 'audio/mp4',
    aac: 'audio/aac',
    wav: 'audio/wav',
    flac: 'audio/flac'
};

// The API's limit.
export const DEMO_MAX_BYTES = 50 * 1024 * 1024;

function audioType(file: File): string | undefined {
    return AUDIO_TYPES[file.name.split('.').pop()?.toLowerCase() ?? ''];
}

// A product's audio demos, played on its page in this order. Each is uploaded straight to the CDN bucket, then
// confirmed, which puts it on the site. Then its waveform and spectrum, for the player to draw, are worked out here from
// the same file (components/demoAnalysis.ts) and sent to the API; for a demo without them, Add waveform does it from the
// file chosen again.
export function ProductDemos({product, onChanged}: { product: ProductViewModel, onChanged(): void }) {
    const [file, setFile] = useState<File>();
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [progress, setProgress] = useState<number>();
    const [editing, setEditing] = useState<{ id: string, title: string, description: string }>();
    const [deleting, setDeleting] = useState<ProductDemoViewModel>();
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<unknown>();
    const [formKey, setFormKey] = useState(0);
    // A demo whose waveform couldn't be worked out (its audio is on the site all the same).
    const [noWaveform, setNoWaveform] = useState<string>();
    // Add waveform: the demo it's for, while its file is being chosen.
    const waveformInput = useRef<HTMLInputElement>(null);
    const [waveformFor, setWaveformFor] = useState<ProductDemoViewModel>();

    const run = async (action: () => Promise<unknown>) => {
        setBusy(true);
        setError(undefined);
        try {
            await action();
            onChanged();
        } catch (err) {
            setError(err);
        } finally {
            setBusy(false);
            setProgress(undefined);
        }
    };

    const fileProblem = !file ? undefined
        : !audioType(file) ? 'Use an MP3, M4A, AAC, WAV or FLAC file.'
        : file.size > DEMO_MAX_BYTES ? `Use a file of at most ${formatBytes(DEMO_MAX_BYTES)}.`
        : undefined;

    const add = (e: FormEvent) => {
        e.preventDefault();
        const contentType = file && audioType(file);
        if (!file || !contentType || fileProblem || !title.trim()) {
            return;
        }
        void run(async () => {
            setProgress(0);
            const {demo, upload} = await ProductsApi.addDemo(product.id, contentType, file.size, title.trim(), description.trim());
            await uploadToPresignedUrl(upload, file, setProgress);
            await ProductsApi.completeDemo(product.id, demo.id);
            await addWaveform(demo, file);
            setFile(undefined);
            setTitle('');
            setDescription('');
            setFormKey(x => x + 1); // clears the file input
        });
    };

    // Not an error that undoes the upload: the player falls back to its plain slider.
    const addWaveform = async (demo: ProductDemoViewModel, audio: File) => {
        setNoWaveform(undefined);
        try {
            await ProductsApi.setDemoAnalysis(product.id, demo.id, await analyseFile(audio));
        } catch {
            setNoWaveform(demo.title);
        }
    };

    const chooseWaveformFile = (demo: ProductDemoViewModel) => {
        setWaveformFor(demo);
        waveformInput.current?.click();
    };

    // The file chosen for Add waveform: it has to be the demo's own (the same size), as only it gives the right picture.
    const waveformFileChosen = (e: ChangeEvent<HTMLInputElement>) => {
        const chosen = e.target.files?.[0];
        e.target.value = '';
        const demo = waveformFor;
        if (!chosen || !demo) {
            return;
        }
        if (chosen.size !== demo.fileSize) {
            setNoWaveform(undefined);
            setError(new Error(`That isn't the file of ${demo.title}: choose the one that was uploaded (${formatBytes(demo.fileSize)}).`));
            return;
        }
        void run(() => addWaveform(demo, chosen));
    };

    const save = (e: FormEvent) => {
        e.preventDefault();
        if (!editing || !editing.title.trim()) {
            return;
        }
        void run(async () => {
            await ProductsApi.updateDemo(product.id, editing.id, { title: editing.title.trim(), description: editing.description.trim() });
            setEditing(undefined);
        });
    };

    // Swaps sort orders with the neighbour, renumbering first so equal orders can't stall it.
    const move = (index: number, by: -1 | 1) => run(async () => {
        const order = [...product.demos];
        [order[index], order[index + by]] = [order[index + by], order[index]];
        for (const [i, demo] of order.entries()) {
            if (demo.sortOrder !== i) {
                await ProductsApi.updateDemo(product.id, demo.id, { sortOrder: i });
            }
        }
    });

    return (
        <Card className="mt-4">
            <Card.Header>Audio demos</Card.Header>
            <Card.Body>
                <ErrorAlert error={error} onClose={() => setError(undefined)}/>
                {noWaveform && (
                    <Alert variant="warning" dismissible onClose={() => setNoWaveform(undefined)}>
                        {noWaveform} is on the site, but its waveform couldn't be worked out from the file, so the player shows a plain slider for it.
                    </Alert>
                )}
                {product.demos.length === 0 && <p className="text-body-secondary">No demos yet.</p>}
            </Card.Body>
            {product.demos.length > 0 && (
                <ListGroup variant="flush">
                    {product.demos.map((demo, index) => (
                        <ListGroup.Item key={demo.id} data-demo={demo.id}>
                            {editing?.id === demo.id ? (
                                <Form onSubmit={save}>
                                    <Form.Group controlId={`demo-title-${demo.id}`} className="mb-2">
                                        <Form.Label>Title</Form.Label>
                                        <Form.Control value={editing.title} required onChange={e => setEditing({ ...editing, title: e.target.value })}/>
                                    </Form.Group>
                                    <Form.Group controlId={`demo-description-${demo.id}`} className="mb-2">
                                        <Form.Label>Description</Form.Label>
                                        <Form.Control as="textarea" rows={2} value={editing.description} onChange={e => setEditing({ ...editing, description: e.target.value })}/>
                                    </Form.Group>
                                    <div className="d-flex gap-2">
                                        <Button type="submit" size="sm" disabled={busy}>Save</Button>
                                        <Button size="sm" variant="outline-secondary" disabled={busy} onClick={() => setEditing(undefined)}>Cancel</Button>
                                    </div>
                                </Form>
                            ) : (
                                <div className="d-flex flex-wrap align-items-start gap-2">
                                    <div className="me-auto">
                                        <strong>{demo.title}</strong>
                                        {!demo.uploaded && <Badge bg="warning" text="dark" className="ms-2">Not uploaded</Badge>}
                                        {demo.uploaded && !demo.hasAnalysis && <Badge bg="secondary" className="ms-2">No waveform</Badge>}
                                        <span className="small text-body-secondary ms-2">{formatBytes(demo.fileSize)}</span>
                                        {demo.description && <div className="small text-body-secondary product-text">{demo.description}</div>}
                                    </div>
                                    {demo.uploaded && !demo.hasAnalysis && (
                                        <Button size="sm" variant="outline-secondary" disabled={busy} onClick={() => chooseWaveformFile(demo)}>
                                            <i className="bi bi-soundwave me-1" aria-hidden="true"></i>Add waveform…
                                        </Button>
                                    )}
                                    <ButtonGroup size="sm">
                                        <Button variant="outline-secondary" disabled={busy || index === 0} onClick={() => move(index, -1)} aria-label="Move up">
                                            <i className="bi bi-arrow-up" aria-hidden="true"></i>
                                        </Button>
                                        <Button variant="outline-secondary" disabled={busy || index === product.demos.length - 1} onClick={() => move(index, 1)} aria-label="Move down">
                                            <i className="bi bi-arrow-down" aria-hidden="true"></i>
                                        </Button>
                                        <Button variant="outline-secondary" disabled={busy} onClick={() => setEditing({ id: demo.id, title: demo.title, description: demo.description })} aria-label="Edit">
                                            <i className="bi bi-pencil" aria-hidden="true"></i>
                                        </Button>
                                        <Button variant="outline-danger" disabled={busy} onClick={() => setDeleting(demo)} aria-label="Delete">
                                            <i className="bi bi-trash" aria-hidden="true"></i>
                                        </Button>
                                    </ButtonGroup>
                                </div>
                            )}
                        </ListGroup.Item>
                    ))}
                </ListGroup>
            )}
            <Card.Body className={product.demos.length > 0 ? 'border-top' : 'pt-0'}>
                <Form key={formKey} onSubmit={add}>
                    <Form.Group controlId="demo-file" className="mb-2">
                        <Form.Control type="file" accept={Object.keys(AUDIO_TYPES).map(x => `.${x}`).join(',')} aria-label="Audio file"
                                      isInvalid={!!fileProblem} aria-invalid={!!fileProblem || undefined} aria-describedby={fileProblem ? 'demo-file-error' : undefined}
                                      onChange={e => setFile((e.target as HTMLInputElement).files?.[0])}/>
                        <Form.Control.Feedback type="invalid" id="demo-file-error">{fileProblem}</Form.Control.Feedback>
                    </Form.Group>
                    <Form.Control className="mb-2" placeholder="Title" aria-label="Title" required value={title} onChange={e => setTitle(e.target.value)}/>
                    <Form.Control className="mb-2" as="textarea" rows={2} placeholder="Description (optional)" aria-label="Description"
                                  value={description} onChange={e => setDescription(e.target.value)}/>
                    <SubmitButton busy={busy}>Add demo</SubmitButton>
                </Form>
                {progress !== undefined && <ProgressBar className="mt-2" now={progress * 100}/>}
            </Card.Body>
            <input ref={waveformInput} type="file" className="d-none" accept={Object.keys(AUDIO_TYPES).map(x => `.${x}`).join(',')}
                   aria-label="The demo's audio file" onChange={waveformFileChosen}/>
            <ConfirmModal show={!!deleting} title={`Delete ${deleting?.title ?? 'this demo'}?`} confirmLabel="Delete" busy={busy}
                          onConfirm={() => { const demo = deleting!; setDeleting(undefined); void run(() => ProductsApi.removeDemo(product.id, demo.id)); }}
                          onCancel={() => setDeleting(undefined)}>
                The audio is deleted from the site, too.
            </ConfirmModal>
        </Card>
    );
}
